import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { search } from '@flow/sync';
import { CommandPalette as Palette, type PaletteResult } from '@flow/ui/components';

import { t } from '../i18n/es.js';
import { useSession } from '../session.jsx';

import { matchesCommand, type Command } from './commands.js';

/** Blocks shown in the palette, and the wait after the last key (design D6 of f1-trash-search). */
const PALETTE_RESULTS = 5;
const DEBOUNCE_MS = 120;

/**
 * The palette of `@flow/ui`, wired to the app: Spanish labels, and the blocks of the local
 * full-text index as a group of their own while typing. Choosing one opens it in the search page's
 * detail panel; «Ver todos los resultados» opens the page with the text.
 */
export function CommandPalette({
  open,
  commands,
  onClose,
}: {
  open: boolean;
  commands: Command[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { local } = useSession();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<PaletteResult[]>([]);

  // A new opening starts clean.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setTerm('');
      setResults([]);
    }
  }

  useEffect(() => {
    if (!open || !local || term.trim() === '') return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      void search(local.db, term, PALETTE_RESULTS).then((hits) => {
        if (cancelled) return;
        setResults(
          hits.map((hit) => ({
            id: hit.block_id,
            title: hit.title,
            kind: t.convert.kinds[hit.kind] ?? hit.kind,
            parts: hit.snippet,
            run: () => void navigate({ to: '/search', search: { q: term, tarea: hit.block_id } }),
          })),
        );
      });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, local, term, navigate]);

  return (
    <Palette
      open={open}
      commands={commands}
      onClose={onClose}
      filter={matchesCommand}
      onSearch={(text) => void navigate({ to: '/search', search: { q: text } })}
      onTermChange={(text) => {
        setTerm(text);
        if (text.trim() === '') setResults([]);
      }}
      results={results}
      labels={{
        title: t.shell.palette.title,
        placeholder: t.shell.palette.placeholder,
        empty: t.shell.palette.empty,
        search: () => t.search.seeAll,
        close: t.shell.palette.close,
        results: t.search.paletteGroup,
      }}
    />
  );
}
